import { useEffect, useMemo, useRef } from 'react'
import { selectCrossClientFilter, selectCrossClientResults, setCrossClientResultsMatchFilter } from '../../../../store/crossClient/crossClientResults'
import { CrossClientQueryResultMatch, CrossClientQueryResultMatches } from '../../../../models/CrossClient'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { MultiSelectDropdown, MultiSelectOption } from '../../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { useSearchParams } from 'react-router-dom'
import './CrossClientFilter.scss'

const matchTitleLabels: Record<CrossClientQueryResultMatch, string> = {
  [CrossClientQueryResultMatches.MATCH]: 'Full',
  [CrossClientQueryResultMatches.PARTIAL]: 'Partial',
  [CrossClientQueryResultMatches.NO_MATCH]: 'No',
}

export const CrossClientMatchFilter = () => {
  const dispatch = useAppDispatch()
  const [searchParams, setSearchParams] = useSearchParams()

  const results = useAppSelector(selectCrossClientResults)
  const filter = useAppSelector(selectCrossClientFilter).matches

  const initialized = useRef(false)

  useEffect(() => {
    if (initialized.current) {
      return
    }
    initialized.current = true
    const matchesParam = searchParams.get('matches')
    if (!matchesParam) {
      return
    }
    const initialMatches = matchesParam
      .split(',')
      .filter((m): m is CrossClientQueryResultMatch => Object.values(CrossClientQueryResultMatches).some((match) => m === match))
    if (initialMatches.length) {
      dispatch(setCrossClientResultsMatchFilter(initialMatches))
    }
  }, [dispatch, searchParams])

  const fullMatchingResults = useMemo(() => results.filter((r) => r.match === CrossClientQueryResultMatches.MATCH).length, [results])
  const partialMatchingResults = useMemo(() => results.filter((r) => r.match === CrossClientQueryResultMatches.PARTIAL).length, [results])
  const notMatchingResults = results.length - fullMatchingResults - partialMatchingResults

  const handleOptionSelected = (selectedIds: string[]) => {
    const selectedMatches = selectedIds as CrossClientQueryResultMatch[]
    dispatch(setCrossClientResultsMatchFilter(selectedMatches))

    setSearchParams(
      (prev) => {
        if (!selectedMatches.length) {
          prev.delete('matches')
        } else {
          prev.set('matches', selectedMatches.join(','))
        }
        return prev
      },
      { replace: true },
    )
  }

  const selectOptions: MultiSelectOption[] = [
    { id: CrossClientQueryResultMatches.MATCH, label: `Full matches (${fullMatchingResults})` },
    { id: CrossClientQueryResultMatches.PARTIAL, label: `Partial matches (${partialMatchingResults})` },
    { id: CrossClientQueryResultMatches.NO_MATCH, label: `No matches (${notMatchingResults})` },
  ]

  const title =
    filter.length === 3
      ? 'All matches'
      : filter.length === 0
        ? 'Select an option'
        : filter.length === 2
          ? `${matchTitleLabels[filter[0]]} + ${matchTitleLabels[filter[1]].toLowerCase()} matches`
          : `${matchTitleLabels[filter[0]]} matches`

  return (
    <div>
      <MultiSelectDropdown
        name='cross-client-multi-select-dropdown'
        className='cross-client-match-filter-dropdown'
        options={selectOptions}
        onSelectedChanged={handleOptionSelected}
        initialSelectedOptions={filter}
        TitleComponent={
          <div className='d-flex gap-2'>
            <i className='bi bi-filter fs-6' />
            {title}
          </div>
        }
        changeOnClose={false}
      />
    </div>
  )
}
